<template>
  <div class="slot-page">
    
    <!-- 헤더 -->
    <header class="page-header">
      <button class="back-btn" @click="goBack" v-if="canGoBack">
        <i class="ph-bold ph-caret-left"></i>
      </button>
      <div class="header-title">
        <h1>🎰 판테크 슬롯머신 이벤트!</h1>
      </div>
      <button class="info-btn" @click="showInfoModal = true">
        <i class="ph-bold ph-question"></i>
      </button>
    </header>

    <!-- 메인 컨텐츠 -->
    <main class="page-content">
      
      <!-- 이벤트 타이틀 -->
      <section class="event-title-section">
        <div class="title-bubble">
          <h2 class="main-title">무조건 당첨!</h2>
          <h3 class="sub-title">두근두근 슬롯머신</h3>
          <p class="subtitle">참여만 하면 <strong>100% 당첨!</strong></p>
        </div>
      </section>

      <!-- 참여 현황 카드 -->
      <section class="status-cards">
        <div class="status-card tickets">
          <div class="card-icon">🎫</div>
          <div class="card-content">
            <p class="card-label">남은 기회</p>
            <p class="card-value">{{ remainingChances }}<span class="unit">회</span></p>
          </div>
        </div>
        <div class="status-card period">
          <div class="card-icon">📅</div>
          <div class="card-content">
            <p class="card-label">이벤트 기간</p>
            <p class="card-value-small">{{ eventPeriod }}</p>
          </div>
        </div>
      </section>

      <!-- 슬롯머신 -->
      <section class="slot-section">
        <div class="slot-wrapper">
          
          <!-- 데코레이션 요소들 -->
          <div class="deco-coins left">
            <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='45' fill='%23FFD700' stroke='%23FFA500' stroke-width='3'/%3E%3Ctext x='50' y='65' text-anchor='middle' fill='%23FFA500' font-size='40' font-weight='bold'%3EP%3C/text%3E%3C/svg%3E" alt="coin" />
          </div>
          <div class="deco-coins right">
            <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='45' fill='%23FFD700' stroke='%23FFA500' stroke-width='3'/%3E%3Ctext x='50' y='65' text-anchor='middle' fill='%23FFA500' font-size='40' font-weight='bold'%3EP%3C/text%3E%3C/svg%3E" alt="coin" />
          </div>

          <!-- 슬롯머신 컨테이너 -->
          <div class="slot-machine">
            
            <!-- 상단 타이틀 -->
            <div class="machine-top">
              <div class="top-light"></div>
              <div class="top-light"></div>
              <div class="top-light"></div>
            </div>

            <!-- 슬롯머신 본체 -->
            <div class="machine-body">
              
              <!-- 디스플레이 영역 -->
              <div class="display-area">
                <div class="display-border">
                  
                  <!-- 슬롯 릴들 -->
                  <div class="reels-container">
                    <div 
                      class="reel" 
                      v-for="(reel, index) in reels" 
                      :key="index"
                      :class="{ spinning: isSpinning }"
                      :style="{ animationDelay: `${index * 0.1}s` }"
                    >
                      <div class="reel-inner" :style="{ transform: `translateY(-${reel.offset}px)` }">
                        <div 
                          class="symbol" 
                          v-for="(symbol, sIndex) in reel.symbols" 
                          :key="sIndex"
                        >
                          {{ symbol }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 페이라인 표시 -->
                  <div class="payline"></div>
                </div>
              </div>

              <!-- 하단 컨트롤 영역 -->
              <div class="control-area">
                <button 
                  class="lever-btn" 
                  @click="spin"
                  :disabled="isSpinning || remainingChances <= 0"
                  :class="{ disabled: remainingChances <= 0 }"
                >
                  <div class="lever">
                    <div class="lever-handle" :class="{ pulled: isSpinning }"></div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 스타트 버튼 -->
        <div class="start-button-wrapper">
          <button 
            class="start-button" 
            @click="spin"
            :disabled="isSpinning || remainingChances <= 0"
            :class="{ disabled: isSpinning || remainingChances <= 0 }"
          >
            <div class="button-content">
              <i class="ph-fill ph-play-circle"></i>
              <span v-if="remainingChances > 0">슬롯머신 돌리기</span>
              <span v-else>참여 완료!</span>
            </div>
          </button>
          <p class="button-help" v-if="remainingChances > 0">
            👆 탭해서 슬롯머신을 돌려보세요!
          </p>
        </div>
      </section>

      <!-- 경품 안내 -->
      <section class="prizes-section">
        <h3 class="section-title">
          <i class="ph-fill ph-gift"></i>
          <span>경품 안내</span>
        </h3>
        <div class="prizes-grid">
          <div 
            class="prize-item" 
            v-for="(prize, index) in prizes" 
            :key="index"
          >
            <div class="prize-item-icon" :style="{ background: prize.color }">
              {{ prize.icon }}
            </div>
            <div class="prize-item-info">
              <p class="prize-item-name">{{ prize.name }}</p>
              <p class="prize-item-desc">{{ prize.description }}</p>
            </div>
            <div class="prize-item-badge">
              {{ prize.probability }}%
            </div>
          </div>
        </div>
      </section>

      <!-- 참여 이력 -->
      <section class="history-section" v-if="userHistory.length > 0">
        <h3 class="section-title">
          <i class="ph-fill ph-clock-clockwise"></i>
          <span>나의 참여 이력</span>
        </h3>
        <div class="history-list">
          <div class="history-item" v-for="(item, index) in userHistory" :key="index">
            <div class="history-icon" :style="{ background: item.color }">
              {{ item.icon }}
            </div>
            <div class="history-info">
              <p class="history-prize">{{ item.prize }}</p>
              <p class="history-date">{{ item.date }}</p>
            </div>
            <div class="history-badge" :class="item.statusClass">
              {{ item.status }}
            </div>
          </div>
        </div>
      </section>

      <!-- 유의사항 -->
      <section class="notice-section">
        <h3 class="section-title">
          <i class="ph-fill ph-warning-circle"></i>
          <span>유의사항</span>
        </h3>
        <div class="notice-box">
          <ul>
            <li>본 이벤트는 {{ eventPeriod }} 동안 진행됩니다.</li>
            <li>1인당 최대 {{ totalChances }}회 참여 가능합니다.</li>
            <li>당첨된 경품은 이벤트 종료 후 순차 지급됩니다.</li>
            <li>부정 참여 적발 시 당첨이 취소될 수 있습니다.</li>
          </ul>
        </div>
      </section>

    </main>

    <!-- 결과 모달 -->
    <transition name="modal">
      <div class="modal-overlay" v-if="showResultModal" @click.self="closeResultModal">
        <div class="result-modal">
          
          <!-- 축하 효과 -->
          <div class="confetti-container" v-if="result?.isWinner">
            <div class="confetti" v-for="n in 20" :key="n"></div>
          </div>

          <div class="result-header">
            <div class="result-emoji">{{ result?.isWinner ? '🎉' : '😢' }}</div>
            <h2>{{ result?.isWinner ? '축하합니다!' : '아쉽네요!' }}</h2>
          </div>

          <div class="result-body">
            <div class="result-prize-icon" :style="{ background: result?.color }">
              {{ result?.icon }}
            </div>
            <h3 class="result-prize-name">{{ result?.name }}</h3>
            <p class="result-prize-desc">{{ result?.description }}</p>

            <div class="result-message">
              <p v-if="result?.isWinner">
                당첨을 진심으로 축하드립니다!<br>
                경품은 등록하신 연락처로 안내드립니다.
              </p>
              <p v-else>
                다음 기회에 다시 도전해주세요!
                <span v-if="remainingChances > 0">
                  <br>아직 {{ remainingChances }}번의 기회가 남았어요!
                </span>
              </p>
            </div>
          </div>

          <div class="result-footer">
            <button class="result-btn share" @click="shareResult">
              <i class="ph-bold ph-share-network"></i>
              공유하기
            </button>
            <button class="result-btn confirm" @click="closeResultModal">
              확인
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 정보 모달 -->
    <transition name="modal">
      <div class="modal-overlay" v-if="showInfoModal" @click.self="showInfoModal = false">
        <div class="info-modal">
          <div class="info-header">
            <h3>이벤트 안내</h3>
            <button @click="showInfoModal = false">
              <i class="ph-bold ph-x"></i>
            </button>
          </div>
          <div class="info-body">
            <div class="info-section">
              <h4>📌 참여 방법</h4>
              <ol>
                <li>"슬롯머신 돌리기" 버튼을 눌러주세요</li>
                <li>슬롯이 멈출 때까지 기다려주세요</li>
                <li>당첨 결과를 확인하세요!</li>
              </ol>
            </div>
            <div class="info-section">
              <h4>🎁 경품 수령</h4>
              <p>당첨 시 등록된 연락처로 수령 방법을 안내드립니다.</p>
            </div>
            <div class="info-section">
              <h4>📞 문의</h4>
              <p>{{ contactInfo }}</p>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- 토스트 -->
    <transition name="toast">
      <div class="toast" v-if="showToast" :class="toastType">
        <i :class="toastIcon"></i>
        <span>{{ toastMessage }}</span>
      </div>
    </transition>

  </div>
</template>

<script>
import { ref, computed, onMounted, inject } from 'vue';
import { useRouter } from 'vue-router';

export default {
  name: 'SlotMachinePage',
  setup() {
    const router = useRouter();
    const sql_query = inject('sql_query', null);
    const getMbid = inject('getMbid', null);

    const eventPeriod = ref('2025.01.01 ~ 2025.01.31');
    const contactInfo = ref('02-1234-5678');
    const totalChances = ref(3);
    const remainingChances = ref(3);
    
    const isSpinning = ref(false);
    const result = ref(null);
    const showResultModal = ref(false);
    const showInfoModal = ref(false);
    const userHistory = ref([]);

    const showToast = ref(false);
    const toastMessage = ref('');
    const toastType = ref('success');
    const toastIcon = computed(() => {
      return toastType.value === 'success' ? 'ph-fill ph-check-circle' : 'ph-fill ph-info';
    });

    const prizes = ref([
      { 
        name: '2,000원 할인쿠폰',
        icon: '🎫',
        description: '전 상품 2,000원 할인',
        probability: 25,
        color: '#FF6B9D',
        isWinner: true,
        value: 1
      },
      { 
        name: '꽝! 다음기회에!',
        icon: '💨',
        description: '아쉽지만 다음에!',
        probability: 20,
        color: '#A0A0A0',
        isWinner: false,
        value: 0
      },
      { 
        name: '2천만원 적금',
        icon: '💰',
        description: '2천만원 적금 상품',
        probability: 5,
        color: '#FFD93D',
        isWinner: true,
        value: 2
      },
      { 
        name: '포토장 쿠폰 3,000원',
        icon: '🎫',
        description: '포토장 3,000원 할인',
        probability: 30,
        color: '#6BCF7F',
        isWinner: true,
        value: 3
      },
      { 
        name: '스타벅스 아메리카노',
        icon: '☕',
        description: '스타벅스 아메리카노 쿠폰',
        probability: 20,
        color: '#8B5CF6',
        isWinner: true,
        value: 4
      },
    ]);

    // 슬롯 심볼들 (실제 상품 아이콘)
    const symbols = computed(() => prizes.value.map(p => p.icon));
    
    // 릴 데이터
    const reels = ref([
      { offset: 0, symbols: [] },
      { offset: 0, symbols: [] },
      { offset: 0, symbols: [] }
    ]);

    // 릴 초기화
    function initReels() {
      reels.value.forEach(reel => {
        reel.symbols = [];
        // 30개 심볼로 증가 (한 화면에 더 많이 보임)
        for (let i = 0; i < 30; i++) {
          reel.symbols.push(symbols.value[Math.floor(Math.random() * symbols.value.length)]);
        }
      });
    }

    const canGoBack = computed(() => window.history.length > 1);

    async function spin() {
      if (isSpinning.value || remainingChances.value <= 0) return;
      
      isSpinning.value = true;
      const selectedPrize = await getRandomPrize();
      
      // 당첨된 상품 아이콘으로 릴 결과 설정
      const winningSymbol = selectedPrize.icon;
      
      // 각 릴 애니메이션 (서로 다른 속도와 회전수)
      reels.value.forEach((reel, index) => {
        const spins = 8 + index * 2; // 각 릴마다 다른 회전 수 증가
        const targetOffset = spins * 100 * 30; // 심볼 높이 * 심볼 개수
        
        setTimeout(() => {
          // CSS transition으로 곡선 효과 적용
          reel.offset = targetOffset;
          
          // 릴이 멈추기 직전에 당첨 심볼로 설정
          const duration = 2000 + (index * 500); // 첫 릴 2초, 두번째 2.5초, 세번째 3초
          setTimeout(() => {
            const visibleIndex = Math.floor(targetOffset / 100) % reel.symbols.length;
            reel.symbols[visibleIndex] = winningSymbol;
          }, duration - 100);
        }, index * 300); // 릴이 순차적으로 시작
      });

      // 결과 표시 (마지막 릴이 멈춘 후)
      setTimeout(() => {
        // 3개가 모두 일치하는지 확인 (잭팟)
        const isJackpot = true; // 릴을 당첨 심볼로 설정했으므로 무조건 잭팟
        
        if (isJackpot) {
          result.value = selectedPrize;
        } else {
          // 잭팟 아니면 꽝 처리
          result.value = prizes.value.find(p => !p.isWinner) || prizes.value[1];
        }
        
        isSpinning.value = false;
        remainingChances.value--;
        saveResult(result.value);
        
        setTimeout(() => {
          showResultModal.value = true;
          if (navigator.vibrate) {
            navigator.vibrate(result.value.isWinner ? [100, 50, 100, 50, 100] : [200]);
          }
        }, 500);
      }, 3500); // 모든 릴이 멈출 때까지 대기
    }

    async function getRandomPrize() {
      const random = Math.random() * 100;
      let cumulative = 0;
      for (const prize of prizes.value) {
        cumulative += prize.probability;
        if (random <= cumulative) return prize;
      }
      return prizes.value[prizes.value.length - 1];
    }

    async function saveResult(prize) {
      userHistory.value.unshift({
        prize: prize.name,
        icon: prize.icon,
        color: prize.color,
        date: new Date().toLocaleString('ko-KR'),
        status: prize.isWinner ? '당첨' : '미당첨',
        statusClass: prize.isWinner ? 'win' : 'lose'
      });

      if (sql_query && getMbid) {
        try {
          await sql_query(`
            INSERT INTO slot_history 
            (mb_id, prize_name, prize_value, is_winner, created_at) 
            VALUES 
            ('${getMbid()}', '${prize.name}', ${prize.value}, ${prize.isWinner ? 1 : 0}, NOW())
          `);
        } catch (error) {
          console.error('Failed to save:', error);
        }
      }
    }

    function closeResultModal() {
      showResultModal.value = false;
      if (remainingChances.value > 0) {
        showToastMessage('아직 기회가 남았어요! 다시 도전해보세요!', 'success');
      }
    }

    function shareResult() {
      const text = `🎰 판테크 슬롯머신 이벤트!\n${result.value.name} 당첨!\n\n지금 바로 참여하세요!`;
      if (navigator.share) {
        navigator.share({ title: '판테크 슬롯머신', text, url: window.location.href })
          .catch(() => copyToClipboard(window.location.href));
      } else {
        copyToClipboard(window.location.href);
      }
    }

    function copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        showToastMessage('링크가 복사되었습니다!', 'success');
      });
    }

    function showToastMessage(msg, type = 'success') {
      toastMessage.value = msg;
      toastType.value = type;
      showToast.value = true;
      setTimeout(() => showToast.value = false, 3000);
    }

    function goBack() {
      canGoBack.value ? router.back() : router.push('/');
    }

    async function loadUserData() {
      if (sql_query && getMbid) {
        try {
          const chances = await sql_query(`
            SELECT COUNT(*) as used 
            FROM slot_history 
            WHERE mb_id = '${getMbid()}' 
            AND DATE(created_at) = CURDATE()
          `);
          if (chances?.[0]) {
            remainingChances.value = totalChances.value - chances[0].used;
          }
        } catch (error) {
          console.error(error);
        }
      }
    }

    onMounted(() => {
      initReels();
      loadUserData();
    });

    return {
      eventPeriod,
      contactInfo,
      totalChances,
      remainingChances,
      isSpinning,
      result,
      prizes,
      reels,
      userHistory,
      showResultModal,
      showInfoModal,
      showToast,
      toastMessage,
      toastType,
      toastIcon,
      canGoBack,
      spin,
      closeResultModal,
      shareResult,
      goBack,
    };
  }
};
</script>

<style scoped>
* {
  -webkit-tap-highlight-color: transparent;
}

.slot-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #6BCF7F 0%, #A4E8B5 100%);
  position: relative;
}

/* 헤더 */
.page-header {
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  background: rgba(107, 207, 127, 0.95);
  backdrop-filter: blur(10px);
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.back-btn,
.info-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 3px solid #2D3E7E;
  background: white;
  color: #2D3E7E;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 800;
  flex-shrink: 0;
}

.back-btn:active,
.info-btn:active {
  transform: scale(0.9);
}

.header-title {
  flex: 1;
  text-align: center;
}

.header-title h1 {
  margin: 0;
  font-size: 18px;
  font-weight: 900;
  color: #2D3E7E;
}

/* 메인 컨텐츠 */
.page-content {
  padding-bottom: 40px;
}

/* 타이틀 섹션 */
.event-title-section {
  padding: 20px 16px;
}

.title-bubble {
  background: linear-gradient(135deg, #2D3E7E 0%, #4A5FA5 100%);
  border-radius: 24px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 8px 24px rgba(45, 62, 126, 0.3);
  border: 4px solid #FFD93D;
}

.main-title {
  margin: 0;
  font-size: 28px;
  font-weight: 900;
  color: #6BDFFF;
  text-shadow: 2px 2px 0 #2D3E7E;
}

.sub-title {
  margin: 8px 0 0;
  font-size: 32px;
  font-weight: 900;
  color: #FFE066;
  text-shadow: 2px 2px 0 #2D3E7E;
}

.subtitle {
  margin: 12px 0 0;
  font-size: 16px;
  color: white;
  font-weight: 700;
}

.subtitle strong {
  color: #FF6B9D;
  font-size: 18px;
}

/* 상태 카드 */
.status-cards {
  padding: 0 16px 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.status-card {
  background: white;
  border: 3px solid #2D3E7E;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 0 #2D3E7E;
}

.card-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.card-content {
  flex: 1;
  min-width: 0;
}

.card-label {
  margin: 0;
  font-size: 12px;
  color: #666;
  font-weight: 600;
}

.card-value {
  margin: 4px 0 0;
  font-size: 24px;
  font-weight: 900;
  color: #2D3E7E;
}

.unit {
  font-size: 14px;
  font-weight: 700;
  margin-left: 2px;
}

.card-value-small {
  margin: 4px 0 0;
  font-size: 13px;
  font-weight: 700;
  color: #2D3E7E;
  line-height: 1.3;
}

/* 슬롯머신 섹션 */
.slot-section {
  padding: 0 16px 32px;
}

.slot-wrapper {
  position: relative;
  padding: 40px 20px;
}

/* 데코레이션 */
.deco-coins {
  position: absolute;
  width: 60px;
  height: 120px;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.deco-coins.left {
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

.deco-coins.right {
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.deco-coins img {
  width: 50px;
  height: 50px;
  animation: coinFloat 3s ease-in-out infinite;
}

.deco-coins img:nth-child(2) {
  animation-delay: 0.5s;
}

@keyframes coinFloat {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(180deg); }
}

/* 슬롯머신 */
.slot-machine {
  width: 100%;
  max-width: 380px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

.machine-top {
  display: flex;
  justify-content: center;
  gap: 20px;
  padding: 16px;
  background: linear-gradient(180deg, #FF6B6B 0%, #FF5252 100%);
  border-radius: 20px 20px 0 0;
  border: 4px solid #2D3E7E;
  border-bottom: none;
}

.top-light {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #FFD93D;
  box-shadow: 0 0 12px #FFD93D;
  animation: blink 1s infinite;
}

.top-light:nth-child(2) {
  animation-delay: 0.3s;
}

.top-light:nth-child(3) {
  animation-delay: 0.6s;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.machine-body {
  background: linear-gradient(180deg, #FF5252 0%, #E53935 100%);
  border: 4px solid #2D3E7E;
  border-radius: 0 0 20px 20px;
  padding: 24px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
}

/* 디스플레이 영역 */
.display-area {
  background: #1A1A1A;
  border-radius: 16px;
  padding: 8px;
  box-shadow: inset 0 4px 12px rgba(0, 0, 0, 0.5);
}

.display-border {
  background: #2D2D2D;
  border: 3px solid #FFD93D;
  border-radius: 12px;
  padding: 16px;
  position: relative;
}

/* 릴 컨테이너 */
.reels-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  background: white;
  padding: 12px;
  border-radius: 8px;
}

.reel {
  background: linear-gradient(180deg, #F5F5F5 0%, #E0E0E0 100%);
  border: 3px solid #2D3E7E;
  border-radius: 8px;
  height: 100px;
  overflow: hidden;
  position: relative;
}

.reel::before,
.reel::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 20px;
  z-index: 2;
  pointer-events: none;
}

.reel::before {
  top: 0;
  background: linear-gradient(to bottom, rgba(255,255,255,0.9), transparent);
}

.reel::after {
  bottom: 0;
  background: linear-gradient(to top, rgba(255,255,255,0.9), transparent);
}

.reel-inner {
  transition: none;
}

.reel:nth-child(1) .reel-inner {
  transition: transform 2s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.reel:nth-child(2) .reel-inner {
  transition: transform 2.5s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.reel:nth-child(3) .reel-inner {
  transition: transform 3s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.reel.spinning .reel-inner {
  transition: none;
  animation: reelSpin 0.1s linear infinite;
}

@keyframes reelSpin {
  from { transform: translateY(0); }
  to { transform: translateY(-100px); }
}

.symbol {
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  background: white;
  border-bottom: 2px solid #E0E0E0;
}

/* 페이라인 */
.payline {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, #FF6B9D 20%, #FF6B9D 80%, transparent);
  transform: translateY(-50%);
  z-index: 3;
  box-shadow: 0 0 8px #FF6B9D;
}

/* 컨트롤 영역 */
.control-area {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.lever-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s;
}

.lever-btn:active:not(.disabled) {
  transform: scale(0.95);
}

.lever-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.lever {
  width: 80px;
  height: 120px;
  background: linear-gradient(180deg, #FFD93D 0%, #FFC107 100%);
  border: 4px solid #2D3E7E;
  border-radius: 40px;
  position: relative;
  box-shadow: 0 6px 0 #2D3E7E;
}

.lever-handle {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #FF6B9D 0%, #FF5252 100%);
  border: 3px solid #2D3E7E;
  border-radius: 50%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  transition: all 0.3s;
}

.lever-handle.pulled {
  top: 60px;
}

.lever::after {
  content: '⬇️';
  position: absolute;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 20px;
}

/* 스타트 버튼 */
.start-button-wrapper {
  margin-top: 24px;
  text-align: center;
}

.start-button {
  width: 100%;
  max-width: 320px;
  padding: 20px;
  border: none;
  border-radius: 50px;
  background: linear-gradient(135deg, #FF6B9D 0%, #FF8FA3 100%);
  border: 4px solid #2D3E7E;
  box-shadow: 0 6px 0 #2D3E7E;
  cursor: pointer;
  transition: all 0.2s;
}

.start-button:active:not(.disabled) {
  transform: translateY(4px);
  box-shadow: 0 2px 0 #2D3E7E;
}

.start-button.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: linear-gradient(135deg, #95A5A6 0%, #7F8C8D 100%);
}

.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: white;
  font-size: 20px;
  font-weight: 900;
  text-shadow: 2px 2px 0 rgba(0, 0, 0, 0.2);
}

.button-content i {
  font-size: 28px;
}

.button-help {
  margin: 12px 0 0;
  font-size: 14px;
  color: #2D3E7E;
  font-weight: 700;
}

/* 섹션 타이틀 */
.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  font-size: 20px;
  font-weight: 900;
  color: #2D3E7E;
  padding: 0 16px;
}

.section-title i {
  font-size: 24px;
}

/* 경품 안내 */
.prizes-section {
  padding: 32px 0;
}

.prizes-grid {
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.prize-item {
  background: white;
  border: 3px solid #2D3E7E;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 0 #2D3E7E;
}

.prize-item-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  flex-shrink: 0;
}

.prize-item-info {
  flex: 1;
  min-width: 0;
}

.prize-item-name {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 800;
  color: #2D3E7E;
}

.prize-item-desc {
  margin: 0;
  font-size: 12px;
  color: #666;
  font-weight: 600;
}

.prize-item-badge {
  padding: 6px 12px;
  background: #FFD93D;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 900;
  color: #2D3E7E;
  flex-shrink: 0;
}

/* 참여 이력 */
.history-section {
  padding: 32px 0;
}

.history-list {
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.history-item {
  background: white;
  border: 3px solid #2D3E7E;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.history-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.history-info {
  flex: 1;
  min-width: 0;
}

.history-prize {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 800;
  color: #2D3E7E;
}

.history-date {
  margin: 0;
  font-size: 11px;
  color: #999;
  font-weight: 600;
}

.history-badge {
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 900;
  flex-shrink: 0;
}

.history-badge.win {
  background: #6BCF7F;
  color: white;
}

.history-badge.lose {
  background: #E0E0E0;
  color: #666;
}

/* 유의사항 */
.notice-section {
  padding: 32px 0 64px;
}

.notice-box {
  margin: 0 16px;
  background: white;
  border: 3px solid #2D3E7E;
  border-radius: 16px;
  padding: 20px;
}

.notice-box ul {
  margin: 0;
  padding-left: 20px;
  list-style: none;
}

.notice-box li {
  position: relative;
  margin-bottom: 12px;
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  font-weight: 600;
}

.notice-box li:last-child {
  margin-bottom: 0;
}

.notice-box li::before {
  content: '•';
  position: absolute;
  left: -16px;
  color: #FF6B9D;
  font-weight: 900;
}

/* 모달 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.result-modal,
.info-modal {
  background: white;
  border-radius: 24px;
  width: 100%;
  max-width: 400px;
  overflow: hidden;
  border: 4px solid #2D3E7E;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.4);
  position: relative;
}

/* 결과 모달 */
.confetti-container {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.confetti {
  position: absolute;
  width: 10px;
  height: 10px;
  background: #FFD93D;
  top: -20px;
  animation: confettiFall 3s linear infinite;
}

.confetti:nth-child(odd) { background: #FF6B9D; }
.confetti:nth-child(3n) { background: #6BDFFF; }
.confetti:nth-child(1) { left: 10%; animation-delay: 0s; }
.confetti:nth-child(2) { left: 20%; animation-delay: 0.2s; }
.confetti:nth-child(3) { left: 30%; animation-delay: 0.4s; }
.confetti:nth-child(4) { left: 40%; animation-delay: 0.1s; }
.confetti:nth-child(5) { left: 50%; animation-delay: 0.3s; }
.confetti:nth-child(6) { left: 60%; animation-delay: 0.5s; }
.confetti:nth-child(7) { left: 70%; animation-delay: 0.15s; }
.confetti:nth-child(8) { left: 80%; animation-delay: 0.35s; }
.confetti:nth-child(9) { left: 90%; animation-delay: 0.25s; }
.confetti:nth-child(10) { left: 15%; animation-delay: 0.45s; }
.confetti:nth-child(11) { left: 25%; animation-delay: 0.55s; }
.confetti:nth-child(12) { left: 35%; animation-delay: 0.65s; }
.confetti:nth-child(13) { left: 45%; animation-delay: 0.05s; }
.confetti:nth-child(14) { left: 55%; animation-delay: 0.75s; }
.confetti:nth-child(15) { left: 65%; animation-delay: 0.85s; }
.confetti:nth-child(16) { left: 75%; animation-delay: 0.95s; }
.confetti:nth-child(17) { left: 85%; animation-delay: 0.12s; }
.confetti:nth-child(18) { left: 95%; animation-delay: 0.22s; }
.confetti:nth-child(19) { left: 5%; animation-delay: 0.32s; }
.confetti:nth-child(20) { left: 48%; animation-delay: 0.42s; }

@keyframes confettiFall {
  to {
    top: 100%;
    transform: rotate(360deg);
  }
}

.result-header {
  padding: 32px 24px 16px;
  text-align: center;
  position: relative;
  z-index: 1;
}

.result-emoji {
  font-size: 64px;
  margin-bottom: 12px;
}

.result-header h2 {
  margin: 0;
  font-size: 28px;
  font-weight: 900;
  color: #2D3E7E;
}

.result-body {
  padding: 0 24px 24px;
  text-align: center;
  position: relative;
  z-index: 1;
}

.result-prize-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  margin: 0 auto 16px;
}

.result-prize-name {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 900;
  color: #2D3E7E;
}

.result-prize-desc {
  margin: 0 0 20px;
  font-size: 14px;
  color: #666;
  font-weight: 600;
}

.result-message {
  background: #F8F9FA;
  border-radius: 12px;
  padding: 16px;
}

.result-message p {
  margin: 0;
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  font-weight: 600;
}

.result-footer {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 24px;
  background: #F8F9FA;
  position: relative;
  z-index: 1;
}

.result-btn {
  padding: 14px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
}

.result-btn.share {
  background: white;
  color: #2D3E7E;
  border: 3px solid #2D3E7E;
}

.result-btn.confirm {
  background: linear-gradient(135deg, #FF6B9D 0%, #FF8FA3 100%);
  color: white;
  border: 3px solid #2D3E7E;
}

.result-btn:active {
  transform: scale(0.95);
}

/* 정보 모달 */
.info-header {
  padding: 20px 24px;
  background: linear-gradient(135deg, #2D3E7E 0%, #4A5FA5 100%);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 900;
  color: white;
}

.info-header button {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.info-header button:active {
  transform: scale(0.9);
}

.info-body {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.info-section {
  margin-bottom: 24px;
}

.info-section:last-child {
  margin-bottom: 0;
}

.info-section h4 {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 800;
  color: #2D3E7E;
}

.info-section ol {
  margin: 0;
  padding-left: 20px;
}

.info-section li {
  margin-bottom: 8px;
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  font-weight: 600;
}

.info-section li:last-child {
  margin-bottom: 0;
}

.info-section p {
  margin: 0;
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  font-weight: 600;
}

/* 토스트 */
.toast {
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(45, 62, 126, 0.95);
  color: white;
  padding: 16px 24px;
  border-radius: 50px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 800;
  font-size: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  border: 3px solid #FFD93D;
  max-width: calc(100% - 40px);
}

.toast i {
  font-size: 20px;
  color: #6BDFFF;
  flex-shrink: 0;
}

/* 애니메이션 */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .result-modal,
.modal-enter-from .info-modal,
.modal-leave-to .result-modal,
.modal-leave-to .info-modal {
  transform: scale(0.8);
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

/* 반응형 */
@media (max-width: 360px) {
  .slot-machine {
    max-width: 320px;
  }

  .reels-container {
    gap: 8px;
    padding: 8px;
  }

  .reel {
    height: 80px;
  }

  .symbol {
    height: 80px;
    font-size: 36px;
  }

  .lever {
    width: 60px;
    height: 100px;
  }

  .lever-handle {
    width: 32px;
    height: 32px;
  }
}

@media (min-width: 768px) {
  .page-content {
    max-width: 600px;
    margin: 0 auto;
  }
}
</style>